import { EmailPanel } from "@/components/admin/EmailPanel";
import { LeadBoard } from "@/components/admin/LeadBoard";
import { RateCardEditor } from "@/components/admin/RateCardEditor";
import { DashboardHeader } from "@/components/admin/dashboard/DashboardHeader";
import { DashboardKpis } from "@/components/admin/dashboard/DashboardKpis";
import { FinanceEnquiryDialog } from "@/components/admin/dashboard/FinanceEnquiryDialog";
import { PipelineSnapshot } from "@/components/admin/dashboard/PipelineSnapshot";
import { QuoteComparisonDialog } from "@/components/admin/dashboard/QuoteComparisonDialog";
import { RecordsPanel } from "@/components/admin/dashboard/RecordsPanel";
import { ServiceBreakdown } from "@/components/admin/dashboard/ServiceBreakdown";
import { SessionDetailDialog } from "@/components/admin/dashboard/SessionDetailDialog";
import { useDashboardData } from "@/components/admin/dashboard/useDashboardData";

/** Sessions, leads, escalations and the email panel. */
const OverviewView = ({ dashboard }) => {
  const { stats } = dashboard;
  return (
    <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <DashboardKpis stats={stats} />

      {stats?.pipeline?.length ? (
        <PipelineSnapshot stats={stats} onOpenBoard={() => dashboard.setView("board")} />
      ) : null}

      <EmailPanel onAuthError={dashboard.handleAuthError} />

      {stats?.by_service?.length ? (
        <ServiceBreakdown stats={stats} maxServiceCount={dashboard.maxServiceCount} />
      ) : null}

      <RecordsPanel
        tab={dashboard.tab}
        onTabChange={dashboard.setTab}
        filters={dashboard.filters}
        rows={dashboard.rows}
        total={dashboard.total}
        loading={dashboard.loading}
        escalationCounts={dashboard.escalationCounts}
        responseClock={dashboard.responseClock}
        savingReference={dashboard.savingReference}
        onExport={dashboard.exportCsv}
        onOpenSession={dashboard.openSession}
        onCompare={dashboard.setCompareLead}
        onOpenFinance={dashboard.setFinanceEnquiry}
        onToggleHandled={dashboard.toggleHandled}
        team={dashboard.team}
        savingOwner={dashboard.savingOwner}
        onAssignOwner={dashboard.assignEscalationOwner}
        onAddTeamMember={dashboard.addTeamMember}
      />

      <p className="mt-6 text-xs leading-relaxed text-muted-foreground">
        Personal information shown here was collected with explicit POPIA consent and is used only
        to prepare and discuss quotes. Please don't share it outside the firm.
      </p>
    </main>
  );
};

/** One of three full-width views, chosen by the header tabs. */
const DashboardView = ({ dashboard }) => {
  if (dashboard.view === "board") {
    return (
      <main className="mx-auto max-w-[1600px] px-2 py-6 sm:px-4">
        <div className="tt-card overflow-hidden">
          <LeadBoard onAuthError={dashboard.handleAuthError} />
        </div>
      </main>
    );
  }
  if (dashboard.view === "rates") {
    return (
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        <div className="tt-card overflow-hidden">
          <RateCardEditor onAuthError={dashboard.handleAuthError} />
        </div>
      </main>
    );
  }
  return <OverviewView dashboard={dashboard} />;
};

/**
 * The staff dashboard.
 *
 * Three views live behind the header tabs — the overview, the follow-up board
 * and the rate card. This file is only the frame: `useDashboardData` owns the
 * state and every backend call, and each block on the overview is its own
 * component under `components/admin/dashboard/`.
 */
export default function AdminDashboardPage() {
  const dashboard = useDashboardData();

  return (
    <div
      data-testid="admin-dashboard-page"
      className="min-h-screen bg-[color:var(--tt-surface-2)]"
    >
      <DashboardHeader
        view={dashboard.view}
        onViewChange={dashboard.setView}
        refreshing={dashboard.refreshing}
        onRefresh={dashboard.refreshAll}
        onSignOut={dashboard.signOut}
        goneQuiet={dashboard.stats?.gone_quiet}
      />

      <DashboardView dashboard={dashboard} />

      <QuoteComparisonDialog
        lead={dashboard.compareLead}
        onClose={() => dashboard.setCompareLead(null)}
      />

      <SessionDetailDialog
        detail={dashboard.detail}
        loading={dashboard.detailLoading}
        onClose={() => dashboard.setDetail(null)}
      />

      <FinanceEnquiryDialog
        enquiry={dashboard.financeEnquiry}
        saving={dashboard.savingFinance}
        onStatusChange={dashboard.setFinanceStatus}
        onClose={() => dashboard.setFinanceEnquiry(null)}
        team={dashboard.team}
        savingOwner={dashboard.savingOwner}
        onAssignOwner={dashboard.assignFinanceOwner}
        onAddTeamMember={dashboard.addTeamMember}
      />
    </div>
  );
}
